import { useEffect, useState } from "react";
import {
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import CrudPage from "components/CrudPage/CrudPage";
import { snack } from "components/Snack/Snack";
import responseTemplatesService from "services/admin/responseTemplatesService";
import {
    ResponseTemplateLanguage,
    RESPONSE_TEMPLATE_LANGUAGES,
    RESPONSE_TEMPLATE_LANGUAGE_LABELS,
} from "interfaces/entities/responseTemplate.model";

// Доступные переменные подсказываются под формой и при выборе шаблона в форме ответа.
// Подстановка делается на клиенте при вставке шаблона в черновик — см. TicketReplyForm.
const TEMPLATE_VARIABLES: Array<{ token: string; description: string }> = [
    { token: "{{customerName}}", description: "Имя клиента (имя + фамилия из тикета)" },
    { token: "{{ticketNumber}}", description: "Номер обращения (ID)" },
];

const ResponseTemplateForm = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const isEdit = Boolean(id);

    const [title, setTitle] = useState("");
    const [content, setContent] = useState("");
    const [language, setLanguage] = useState<ResponseTemplateLanguage>(ResponseTemplateLanguage.RU);
    const [isLoading, setIsLoading] = useState(isEdit);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!id) return;
        let cancelled = false;
        (async () => {
            try {
                setIsLoading(true);
                const response: any = await responseTemplatesService.get(id);
                const data = response?.data ?? response;
                if (cancelled || !data) return;
                setTitle(data.title || "");
                setContent(data.content || "");
                if (RESPONSE_TEMPLATE_LANGUAGES.includes(data.language)) {
                    setLanguage(data.language);
                }
            } catch (e) {
                console.error(e);
                snack("Не удалось загрузить шаблон");
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [id]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const trimmedTitle = title.trim();
        const trimmedContent = content.trim();
        if (!trimmedTitle) {
            snack("Укажите название шаблона");
            return;
        }
        if (!trimmedContent) {
            snack("Шаблон не может быть пустым");
            return;
        }
        try {
            setIsSaving(true);
            const payload = { title: trimmedTitle, content, language };
            if (isEdit && id) {
                await responseTemplatesService.put(id, payload as any);
                snack("Шаблон обновлён");
            } else {
                await responseTemplatesService.post(payload);
                snack("Шаблон создан");
            }
            setTimeout(() => navigate(-1), 400);
        } catch (e) {
            console.error(e);
            snack("Не удалось сохранить шаблон");
        } finally {
            setIsSaving(false);
        }
    };

    if (isLoading) {
        return (
            <CrudPage title="Загрузка...">
                <div className="text-center">
                    <CircularProgress />
                </div>
            </CrudPage>
        );
    }

    return (
        <CrudPage title={isEdit ? "Редактирование шаблона" : "Создание шаблона"}>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4" style={{ maxWidth: 720 }}>
                <TextField
                    label="Название"
                    placeholder="Например: Виза готова"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    fullWidth
                    disabled={isSaving}
                />

                <FormControl fullWidth disabled={isSaving}>
                    <InputLabel>Язык</InputLabel>
                    <Select
                        label="Язык"
                        value={language}
                        onChange={(e) => setLanguage(e.target.value as ResponseTemplateLanguage)}
                    >
                        {RESPONSE_TEMPLATE_LANGUAGES.map((lang) => (
                            <MenuItem key={lang} value={lang}>
                                {RESPONSE_TEMPLATE_LANGUAGE_LABELS[lang]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Текст шаблона"
                    placeholder="Здравствуйте, {{customerName}}! Ваша виза готова..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                    required
                    multiline
                    minRows={8}
                    maxRows={20}
                    fullWidth
                    disabled={isSaving}
                />

                <div style={{ background: "#f6f8fa", padding: "12px 16px", borderRadius: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                        Доступные переменные:
                    </div>
                    <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: "#555" }}>
                        {TEMPLATE_VARIABLES.map((v) => (
                            <li key={v.token}>
                                <code>{v.token}</code> — {v.description}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="flex gap-2 items-center">
                    <Button type="submit" variant="contained" color="primary" disabled={isSaving}>
                        {isSaving ? "Сохранение..." : isEdit ? "Сохранить" : "Создать"}
                    </Button>
                    <Button type="button" variant="outlined" onClick={() => navigate(-1)} disabled={isSaving}>
                        Отмена
                    </Button>
                </div>
            </form>
        </CrudPage>
    );
};

export default ResponseTemplateForm;
